import { Injectable } from '@angular/core';
import { Observable, Subscriber, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { Location, AirPollutionResult, AirPollutionNetwork, CustomInfiltrationResult, PurpleAirMonitorResult
 } from '../models/types';

@Injectable({
  providedIn: 'root'
})
export class AirPollutionService {
  
  radius = 100000;
  
  constructor(
    private http: HttpClient
  ) { }
  
  fetch24HourAverages(latitude: number, longitude: number, network: AirPollutionNetwork, startDate: Date, endDate: Date, numDays: number):  Observable<AirPollutionResult[]> {
    return new Observable<AirPollutionResult[]>(subscriber => {
      if (network == AirPollutionNetwork.purpleAir) {
        this.fetchAverageFromPurpleAir(latitude, longitude, startDate, endDate, numDays, subscriber);
      } else {
        this.fetchAverageFromAirNow(latitude, longitude, startDate, endDate, numDays, subscriber);
      }
    });
  }
  
  private formatDateForAirNow(date: Date): string {
    return date.toISOString().replace(/:\d+:\d+.\d+Z$/, '');
  }
  
  private fetchAverageFromAirNow(latitude: number, longitude: number, startDate: Date, endDate: Date, numDays: number, subscriber: Subscriber<AirPollutionResult[]>) {
    // calculate bounding box
    let distance = 100000; // meters
    distance = distance * Math.sqrt(2);
    const swCorner = google.maps.geometry.spherical.computeOffset({lat: latitude, lng: longitude}, distance, 225);
    const neCorner = google.maps.geometry.spherical.computeOffset({lat: latitude, lng: longitude}, distance, 45);
    
    const url = `https://www.airnowapi.org/aq/data/?` +
    `API_KEY=855853A5-6FD3-4888-8EC1-AF736F63CC95&` +
    `startDate=${this.formatDateForAirNow(startDate)}&` +
    `endDate=${this.formatDateForAirNow(endDate)}&` +
    `BBOX=${swCorner.lng().toFixed(6)},${swCorner.lat().toFixed(6)},` + 
    `${neCorner.lng().toFixed(6)},${neCorner.lat().toFixed(6)}&` +
    `parameters=OZONE,PM25&dataType=C&format=application/json&` +
    `verbose=0&includerawconcentrations=1`;
    console.log(url);
    this.http.get<{[key: string]: any}[]>(url).subscribe(results => {
      if (results.length == 0) {
        console.log('No monitors found');
        subscriber.error('No monitors');
        return;
      }
      
      const ozoneDistances: [number, string][] = []; // distance, location
      const ozoneValues: {[key: string]: any} = {}; // key = location
      const pm25Distances: [number, string][] = [];
      const pm25Values: {[key: string]: any} = {};
      for (const item of results) {
        // skip any observation values less than zero
        const value = item['RawConcentration'];
        if (value < 0) continue;
        
        const resultDate = new Date(item['UTC'] + ':00+00:00');
        const dayNum = Math.floor((endDate.getTime() - resultDate.getTime())/(1000*60*60*24))
        if (dayNum > (numDays - 1) || dayNum < 0) continue;
        
        const location = `${item['Latitude']} ${item['Longitude']}`;
        if (item['Parameter'] == 'OZONE') {
          if (ozoneValues[location] === undefined) {
            ozoneValues[location] = {}
            const distance = google.maps.geometry.spherical.computeDistanceBetween({lat: item['Latitude'], lng: item['Longitude']}, {lat: latitude, lng: longitude});
            ozoneDistances.push([distance, location]);
          }
          if (ozoneValues[location][dayNum] === undefined) {
            ozoneValues[location][dayNum] = {
              'ozone': 0,
              'numObs': 0
            }
          }
          ozoneValues[location][dayNum]['ozone'] += value;
          ozoneValues[location][dayNum]['numObs'] += 1;
          
        } else {
          if (pm25Values[location] === undefined) {
            pm25Values[location] = {}
            const distance = google.maps.geometry.spherical.computeDistanceBetween({lat: item['Latitude'], lng: item['Longitude']}, {lat: latitude, lng: longitude});
            pm25Distances.push([distance, location]);
          }
          if (pm25Values[location][dayNum] === undefined) {
            pm25Values[location][dayNum] = {
              'pm25': 0,
              'numObs': 0
            }
          }
          pm25Values[location][dayNum]['pm25'] += value;
          pm25Values[location][dayNum]['numObs'] += 1;
        }
      }
      
      // find closest ozone monitor with valid data
      ozoneDistances.sort((a, b) => {
        return a[0] - b[0];
      });
      let ozoneLocation = '';
      for (const item of ozoneDistances) {
        let missingData = false;
        for (const dayNumKey of Object.keys(ozoneValues[item[1]])) {
          const ozoneValue = ozoneValues[item[1]][dayNumKey];
          if (ozoneValue['numObs'] < 18) {
            missingData = true;
            break;
          }
        }
        
        if (!missingData) {
          ozoneLocation = item[1];
          break;
        }
      }
      
      // find closest PM2.5 monitor with valid data
      pm25Distances.sort((a, b) => {
        return a[0] - b[0];
      });
      let pm25Location = '';
      for (const item of pm25Distances) {
        let missingData = false;
        for (const dayNumKey of Object.keys(pm25Values[item[1]])) {
          const pm25Value = pm25Values[item[1]][dayNumKey];
          if (pm25Value['numObs'] < 18) {
            missingData = true;
            break;
          }
        }
        
        if (!missingData) {
          pm25Location = item[1];
          break;
        }
      }
      
      // build results array
      let airPollutionResults: AirPollutionResult[] = [];
      if (ozoneLocation != '' && pm25Location != '') {
        for (const dayNumKey of Object.keys(ozoneValues[ozoneLocation])) {
          const ozoneValue = ozoneValues[ozoneLocation][dayNumKey];
          const pm25Value = pm25Values[pm25Location][dayNumKey];
          const obsDate = new Date(endDate);
          obsDate.setDate(obsDate.getDate() - Number(dayNumKey));
          airPollutionResults.push({
            hour: obsDate,
            pm25: pm25Value['pm25'] / pm25Value['numObs'],
            ozone: ozoneValue['ozone'] / ozoneValue['numObs']
          });
        }
      }
      
      console.log('sending AirNow result');
      if (airPollutionResults.length > 0) {
        subscriber.next(airPollutionResults);
      } else {
        subscriber.error();
      }
    });
  }
  
  private formatDateForPurpleAir(date: Date): string {
    return date.toISOString().replace(/.\d+Z$/, 'Z');
  }
  
  private buildPurpleAirUrl(startDate: Date, endDate: Date, swCorner: Location, neCorner: Location): string {
    return `https://ofmpub.epa.gov/rsig/rsigserver?` +
    `KEY=9E69C44C-3EFD-11EB-9893-42010A8001E8&` +
    `SERVICE=wcs&VERSION=1.0.0&REQUEST=GetCoverage&FORMAT=ascii&` +
    `TIME=${this.formatDateForPurpleAir(startDate)}/` +
    `${this.formatDateForPurpleAir(endDate)}&` +
    `BBOX=${swCorner.longitude.toFixed(6)},${swCorner.latitude.toFixed(6)},` + 
    `${neCorner.longitude.toFixed(6)},${neCorner.latitude.toFixed(6)}&`;
  }
  
  private fetchAverageFromPurpleAir(latitude: number, longitude: number, startDate: Date, endDate: Date, numDays: number, subscriber: Subscriber<AirPollutionResult[]>) {
    startDate.setDate(startDate.getDate() + 5);
    // calculate bounding box
    let distance = 100000; // meters
    distance = distance * Math.sqrt(2);
    const swCorner = google.maps.geometry.spherical.computeOffset({lat: latitude, lng: longitude}, distance, 225);
    const swCornerLoc = { latitude: swCorner.lat(), longitude: swCorner.lng() };
    const neCorner = google.maps.geometry.spherical.computeOffset({lat: latitude, lng: longitude}, distance, 45);
    const neCornerLoc = { latitude: neCorner.lat(), longitude: neCorner.lng() };
    
    const url = this.buildPurpleAirUrl(startDate, endDate, swCornerLoc, neCornerLoc) +
    `COVERAGE=purpleair.pm25_corrected&` +
    `OUT_IN_FLAG=0&` +
    `AGGREGATE=hourly`;
    console.log(url);
    this.http.get<string>(url, {responseType: 'text' as 'json'}).subscribe(results => {
      const pm25Distances: [number, string][] = [];
      const pm25Values: {[key: string]: any} = {};
      
      let header = true;
      for (const line of results.split(/\n/)) {
        if (header) {
          header = false;
          continue;
        }
      
        const fields = line.split(/\t/);
        const resultDate = new Date(fields[0]);
        const dayNum = Math.floor((endDate.getTime() - resultDate.getTime())/(1000*60*60*24));
        if (dayNum > (numDays - 1) || dayNum < 0) continue;
        const obsLong = parseFloat(fields[1]);
        const obsLat = parseFloat(fields[2]);
        const value = parseFloat(fields[6]);
        const location = `${obsLat} ${obsLong}`;
        if (pm25Values[location] === undefined) {
          pm25Values[location] = {}
          const distance = google.maps.geometry.spherical.computeDistanceBetween({lat: obsLat, lng: obsLong}, {lat: latitude, lng: longitude});
          pm25Distances.push([distance, location]);
        }
        if (pm25Values[location][dayNum] === undefined) {
          pm25Values[location][dayNum] = {
            'pm25': 0,
            'numObs': 0
          }
        }
        pm25Values[location][dayNum]['pm25'] += value;
        pm25Values[location][dayNum]['numObs'] += 1;
      }
    
      // find closest PM2.5 monitor with valid data
      pm25Distances.sort((a, b) => {
        return a[0] - b[0];
      });
      let pm25Location = '';
      for (const item of pm25Distances) {
        let missingData = false;
        for (const dayNumKey of Object.keys(pm25Values[item[1]])) {
          const pm25Value = pm25Values[item[1]][dayNumKey];
          if (pm25Value['numObs'] < 18) {
            missingData = true;
            break;
          }
        }
        
        if (!missingData) {
          pm25Location = item[1];
          break;
        }
      }
      
      // build results array
      let airPollutionResults: AirPollutionResult[] = [];
      if (pm25Location != '') {
        for (const dayNumKey of Object.keys(pm25Values[pm25Location])) {
          const pm25Value = pm25Values[pm25Location][dayNumKey];
          const obsDate = new Date(endDate);
          obsDate.setDate(obsDate.getDate() - Number(dayNumKey));
          airPollutionResults.push({
            hour: obsDate,
            pm25: pm25Value['pm25'] / pm25Value['numObs'],
            ozone: 0
          });
        }
      }
    
      console.log('sending PurpleAir result');
      subscriber.next(airPollutionResults);
    }, err => {
      console.log(err);
      console.log('No monitors found');
      return;
    });
  }
  
  fetchCustomInfiltrationFromPurpleAir(locations: Location[], outdoorRadius: number, indoorRadius: number, startDate: Date, endDate: Date, temperatureAdjustment: number): Observable<CustomInfiltrationResult[]> {
    return new Observable<CustomInfiltrationResult[]>(subscriber => {
      let requests = [];
      for (const location of locations) {
        requests.push(this.buildCustomInfiltrationRequest(location, outdoorRadius, indoorRadius, startDate, endDate));
      }
      forkJoin(requests).subscribe(results => {
        subscriber.next(this.processCustomInfiltrationResults(locations, temperatureAdjustment, results));
      });
    });
  }
  
  private buildCustomInfiltrationRequest(location: Location, outdoorRadius: number, indoorRadius: number, startDate: Date, endDate: Date): Observable<{[key: string]: any}> {
    let queryData = {
      start_date: this.formatDateForPurpleAir(startDate),
      end_date: this.formatDateForPurpleAir(endDate),
      outdoor_latitude: location.latitude,
      outdoor_longitude: location.longitude,
      outdoor_radius: outdoorRadius,
      indoor_latitude: location.latitude,
      indoor_longitude: location.longitude,
      indoor_radius: indoorRadius,
      custom_infiltration: true,
      allow_single: true
    };
    const url = `https://treehug-app.its.unc.edu/tracmyair/purpleair/observations/multiple/`;
    return this.http.post<{[key: string]: any}>(url, queryData);
  }
  
  private processCustomInfiltrationResults(locations: Location[], temperatureAdjustment: number, results: {[key: string]: any}): CustomInfiltrationResult[] {
    let output: CustomInfiltrationResult[] = [];
    for (let i = 0; i < locations.length; i++) {
      const location = locations[i];
      const result = results[i];
      
      if (Object.keys(result).length == 0) {
        continue;
      }
      
      const outdoor = result['Outdoor'];
      if (Object.keys(outdoor).length == 0) {
        continue;
      }
      const indoor = result['Indoor'];
      
      const baseResult: CustomInfiltrationResult = {
        inputLatitude: location.latitude,
        inputLongitude: location.longitude,
        hour: new Date(),
        outdoorLatitude: outdoor['Latitude'],
        outdoorLongitude: outdoor['Longitude'],
        outdoorSingleSensor: outdoor['SingleSensor'],
        outdoorPM25: -999,
        outdoorTemperature: -999,
        indoorLatitude: -999,
        indoorLongitude: -999,
        indoorSingleSensor: false,
        indoorPM25: -999,
        indoorTemperature: -999,
        infiltrationFactor: -999,
        indoorConcentration: -999,
        infiltrationFactorAverage: -999
      };
      
      let locationOutput: CustomInfiltrationResult[] = [];
      for (const hourly of outdoor['Observations']) {
        let hourlyResult = {...baseResult};
        hourlyResult.hour = new Date(hourly['Date']);
        hourlyResult.outdoorPM25 = hourly['PM25'];
        hourlyResult.outdoorTemperature = hourly['Temperature'] + temperatureAdjustment;
        locationOutput.push(hourlyResult);
      }
      
      if (Object.keys(indoor).length > 0) {
        let hourIndex = 0;
        for (const hourly of indoor['Observations']) {
          let hourlyResult = locationOutput[hourIndex];
          if (hourlyResult.hour = new Date(hourly['Date'])) {
            hourlyResult.indoorLatitude = indoor['Latitude'];
            hourlyResult.indoorLongitude = indoor['Longitude'];
            hourlyResult.indoorSingleSensor = indoor['SingleSensor'];
            hourlyResult.indoorPM25 = hourly['PM25'];
            hourlyResult.indoorTemperature = hourly['Temperature'] + temperatureAdjustment;
            hourlyResult.infiltrationFactor = hourly['Infiltration Factor'];
            hourlyResult.indoorConcentration = hourly['Indoor Concentration'];
            hourlyResult.infiltrationFactorAverage = hourly['Infiltration Factor 2'];
          }
          hourIndex += 1;
        }
      }
      
      output = output.concat(locationOutput);
    }
    return output;
  }
  
  fetchOutdoorPurpleAirMonitors(swCorner: Location, neCorner: Location): Observable<PurpleAirMonitorResult[]> {
    let startDate = new Date();
    startDate.setDate(startDate.getDate() - 2);
    startDate.setUTCHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);
    let endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    endDate.setUTCHours(0);
    endDate.setMinutes(0);
    endDate.setSeconds(0);
    
    const url = this.buildPurpleAirUrl(startDate, endDate, swCorner, neCorner) +
    `COVERAGE=purpleair.pm25_corrected_daily`;
    console.log(url);
    return new Observable<PurpleAirMonitorResult[]>(subscriber => {
      this.http.get<string>(url, {responseType: 'text' as 'json'}).subscribe(results => {
        let monitors: PurpleAirMonitorResult[] = [];
        let header = true;
        for (const line of results.split(/\n/)) {
          if (header) {
            header = false;
            continue;
          }
      
          const fields = line.split(/\t/);
          monitors.push({
            id: 0,
            label: fields[7],
            latitude: parseFloat(fields[2]),
            longitude: parseFloat(fields[1]),
            inside: false,
            single: false
          });
        }
        subscriber.next(monitors);
      }, err => {
      });
    });
  }
  
  fetchIndoorPurpleAirMonitors(swCorner: Location, neCorner: Location): Observable<PurpleAirMonitorResult[]> {
    let startDate = new Date();
    startDate.setHours(startDate.getHours() - 8);
    let endDate = new Date();
    
    const url = this.buildPurpleAirUrl(startDate, endDate, swCorner, neCorner) +
    `COVERAGE=purpleair.pm25_a&` +
    `OUT_IN_FLAG=1&` +
    `AGGREGATE=all`;
    console.log(url);
    return new Observable<PurpleAirMonitorResult[]>(subscriber => {
      this.http.get<string>(url, {responseType: 'text' as 'json'}).subscribe(results => {
        let monitors: PurpleAirMonitorResult[] = [];
        let header = true;
        for (const line of results.split(/\n/)) {
          if (header) {
            header = false;
            continue;
          }
      
          const fields = line.split(/\t/);
          monitors.push({
            id: 0,
            label: fields[7],
            latitude: parseFloat(fields[2]),
            longitude: parseFloat(fields[1]),
            inside: true,
            single: false
          });
        }
        subscriber.next(monitors);
      }, err => {
      });
    });
  }
}
